import { createRef, useContext, useRef, useState } from "react";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  QueryFilter,
  PersonFilterKeys,
  IPersonPaabudFilterSetting,
} from "../../../../interfaces/filters";
import { AccordionFilterItem } from "../../../accordion/Accordion";
import {
  EfteruddannelseIndberetningStatusId,
  PersonDtoFilterInput,
  PersonPaabudStatusId,
} from "../../../../interfaces/schema";
import InputCheckbox from "../../../formcontrols/InputCheckbox";
import { random, startCase } from "lodash";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../select/Select";
import {
  dateQueries,
  numberQueries,
} from "../../../../config/filter/CustomFilterConfig";
import { ComboBox } from "../../../filter/ComboBox";
import { DatePicker, useToaster, Notification } from "rsuite";
import { isCompleteDate } from "../../../formcontrols/datePickerValue";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import { format } from "date-fns";
import { f } from "../../../../util/f";
import WarningIcon from "../../../icons/WarningIcon";

const filterKey: QueryFilter<PersonFilterKeys>["filterKey"] = "paabud";

const paabudStatusLabels: Record<PersonPaabudStatusId, string> = {
  [PersonPaabudStatusId.Aabent]: "Åben",
  [PersonPaabudStatusId.Lukket]: "Afsluttet",
  [PersonPaabudStatusId.Slettet]: "Slettet",
};

export default function PersonPaabudFilter() {
  const iBeroCheckboxRef = useRef<HTMLInputElement>(null);
  const filters = useContext(PersonFiltersContext);
  const updateFilters = useContext(PersonFiltersEventContext);

  const settings: IPersonPaabudFilterSetting = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  const paabudStatusCheckboxRefs = useRef(
    [...Array(Object.keys(PersonPaabudStatusId).length)].map(() =>
      createRef<HTMLInputElement>(),
    ),
  );
  const indberetningStatusCheckboxRefs = useRef(
    [...Array(Object.keys(EfteruddannelseIndberetningStatusId).length)].map(
      () => createRef<HTMLInputElement>(),
    ),
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const inputToRef = useRef<HTMLInputElement>(null);

  const [selectedFristDateQuery, setSelectedFristDateQuery] = useState(
    dateQueries[0],
  );
  const [selectedLektionerQuery, setSelectedLektionerQuery] = useState(
    numberQueries[0],
  );
  const [selectedReceivedDateQuery, setSelectedReceivedDateQuery] = useState(
    dateQueries[0],
  );

  const [isLektionerBetween, setIsLektionerBetween] = useState<boolean>(false);
  const [isFristDateBetween, setIsFristDateBetween] = useState<boolean>(false);
  const [isReceivedDateBetween, setIsReceivedDateBetween] =
    useState<boolean>(false);

  const [selectedFristDateFrom, setSelectedFristDateFrom] =
    useState<Date | null>(new Date());
  const [selectedFristDateTo, setSelectedFristDateTo] = useState<Date | null>(
    new Date(),
  );

  const [selectedReceivedDateFrom, setSelectedReceivedDateFrom] =
    useState<Date | null>(new Date());
  const [selectedReceivedDateTo, setSelectedReceivedDateTo] =
    useState<Date | null>(new Date());

  const toaster = useToaster();

  function getCheckboxValues<T extends string>(
    refs: ReturnType<typeof createRef<HTMLInputElement>>[],
  ): Array<T> {
    return refs.flatMap((r) => {
      if (r.current?.checked) {
        return r.current.id.substring(0, r.current.id.indexOf("__")) as T;
      }
      return [];
    });
  }

  function buildFilter(updatedSettings: IPersonPaabudFilterSetting) {
    return f<PersonDtoFilterInput>()
      .addFilter("personPaabud", {
        some: {
          and: [
            updatedSettings.paabudStatus.length
              ? {
                  status: {
                    in: updatedSettings.paabudStatus,
                  },
                }
              : {},

            updatedSettings.indberetningStatus.length
              ? {
                  personPaabudIndberetning: {
                    some: {
                      efteruddannelseIndberetningStatus: {
                        in: updatedSettings.indberetningStatus,
                      },
                    },
                  },
                }
              : {},

            updatedSettings.antalLektioner.length
              ? {
                  or: updatedSettings.antalLektioner.map((item) => item.filter),
                }
              : {},

            updatedSettings.modtagetDatoer.length
              ? {
                  or: [
                    {
                      personPaabudIndberetning: {
                        some: {
                          or: updatedSettings.modtagetDatoer.map(
                            (item) => item.filter,
                          ),
                        },
                      },
                    },
                  ],
                }
              : {},

            updatedSettings.fristDatoer.length
              ? {
                  or: [
                    {
                      personPaabudIndberetning: {
                        some: {
                          or: updatedSettings.fristDatoer.map(
                            (item) => item.filter,
                          ),
                        },
                      },
                    },
                  ],
                }
              : {},

            // Bero har ingen egen kolonne: fristen mangler, netop fordi den ikke løber.
            updatedSettings.iBero
              ? {
                  personPaabudIndberetning: {
                    some: {
                      indberetningFrist: { eq: null },
                    },
                  },
                }
              : {},
          ],
        },
      })
      .build();
  }

  function updateFilterHandler(
    eventType:
      | "paabudStatus"
      | "indberetningStatus"
      | "lessons"
      | "receivedDate"
      | "fristDate"
      | "iBero",
  ) {
    const paabudStatusValues = getCheckboxValues<PersonPaabudStatusId>(
      paabudStatusCheckboxRefs.current,
    );
    const indberetningStatusValues =
      getCheckboxValues<EfteruddannelseIndberetningStatusId>(
        indberetningStatusCheckboxRefs.current,
      );

    const updatedLektionerSettings = settings?.antalLektioner || [];

    if (eventType === "lessons") {
      let lektionerItem:
        IPersonPaabudFilterSetting["antalLektioner"][number] | undefined;

      if (isLektionerBetween) {
        const inputFromValue = inputRef.current?.value;
        const inputToValue = inputToRef.current?.value;

        if (inputFromValue && inputToValue) {
          lektionerItem = {
            key: `gt-${parseInt(inputFromValue)}-lt-${parseInt(inputToValue)}`,
            selectedQueryName: "Mellem",
            displayName: `${parseInt(inputFromValue)} - ${parseInt(
              inputToValue,
            )}`,
            filter: {
              and: [
                { lektionerGennemfoert: { gt: parseInt(inputFromValue) } },
                { lektionerGennemfoert: { lt: parseInt(inputToValue) } },
              ],
            },
          };
        }
      } else {
        const inputValue = inputRef.current?.value;
        if (inputValue) {
          lektionerItem = {
            key: `${selectedLektionerQuery.query}-${parseInt(inputValue)}`,
            selectedQueryName: selectedLektionerQuery.displayName,
            displayName: inputValue,
            filter: {
              lektionerGennemfoert: {
                [selectedLektionerQuery.query]: parseInt(inputValue),
              },
            },
          };
        }
      }

      if (lektionerItem) {
        if (
          !settings?.antalLektioner.some(
            (item) => item.key === lektionerItem?.key,
          )
        ) {
          updatedLektionerSettings.push(lektionerItem);
        } else {
          toaster.push(
            <Notification>
              <div className="d-flex flex-row gap-3 align-items-center">
                <span className={`c-theme-icon text-light bg-warning`}>
                  <WarningIcon />
                </span>
                Filteret er allerede tilføjet.
              </div>
            </Notification>,
            {
              placement: "bottomEnd",
              duration: 2500,
            },
          );
        }
      }
    }

    const updatedRegDatoSettings = settings?.modtagetDatoer || [];

    if (eventType === "receivedDate") {
      // OK is disabled while the date is unfinished; the to-date is only
      // read when "between" is chosen (ADV-320).
      if (!selectedReceivedDateFrom) return;
      const selectedReceivedDateFromFormattedPretty = format(
        selectedReceivedDateFrom,
        "dd-MM-yyyy",
      );
      const selectedReceivedDateFromFormattedFilter = format(
        selectedReceivedDateFrom,
        "yyyy-MM-dd",
      );
      const selectedReceivedDateToFormattedPretty = format(
        selectedReceivedDateTo ?? selectedReceivedDateFrom,
        "dd-MM-yyyy",
      );
      const selectedReceivedDateToFormattedFilter = format(
        selectedReceivedDateTo ?? selectedReceivedDateFrom,
        "yyyy-MM-dd",
      );

      let receivedDateItem: IPersonPaabudFilterSetting["modtagetDatoer"][number];

      if (isReceivedDateBetween) {
        receivedDateItem = {
          key: `gt-${selectedReceivedDateFromFormattedPretty}-lt-${selectedReceivedDateToFormattedPretty}`,
          displayName: `${selectedReceivedDateFromFormattedPretty} - ${selectedReceivedDateToFormattedPretty}`,
          selectedQueryName: "Mellem",
          filter: {
            and: [
              {
                registreringsDato: {
                  gt: selectedReceivedDateFromFormattedFilter,
                },
              },
              {
                registreringsDato: {
                  lt: selectedReceivedDateToFormattedFilter,
                },
              },
            ],
          },
        };
      } else {
        receivedDateItem = {
          key: `${selectedReceivedDateQuery.query}-${selectedReceivedDateFromFormattedPretty}`,
          displayName: selectedReceivedDateFromFormattedPretty,
          selectedQueryName: selectedReceivedDateQuery.displayName,
          filter: {
            registreringsDato: {
              [selectedReceivedDateQuery.query]:
                selectedReceivedDateFromFormattedFilter,
            },
          },
        };
      }

      if (
        !updatedRegDatoSettings.some(
          (item) => item.key === receivedDateItem.key,
        )
      ) {
        updatedRegDatoSettings.push(receivedDateItem);
      } else {
        toaster.push(
          <Notification>
            <div className="d-flex flex-row gap-3 align-items-center">
              <span className={`c-theme-icon text-light bg-warning`}>
                <WarningIcon />
              </span>
              Filteret er allerede tilføjet.
            </div>
          </Notification>,
          {
            placement: "bottomEnd",
            duration: 2500,
          },
        );
      }
    }

    const updatedFristDateSettings = settings?.fristDatoer || [];

    if (eventType === "fristDate") {
      // OK is disabled while the date is unfinished; the to-date is only
      // read when "between" is chosen (ADV-320).
      if (!selectedFristDateFrom) return;
      const selectedFristDateFromFormattedPretty = format(
        selectedFristDateFrom,
        "dd-MM-yyyy",
      );
      const selectedFristDateFromFormattedFilter = format(
        selectedFristDateFrom,
        "yyyy-MM-dd",
      );
      const selectedFristDateToFormattedPretty = format(
        selectedFristDateTo ?? selectedFristDateFrom,
        "dd-MM-yyyy",
      );
      const selectedFristDateToFormattedFilter = format(
        selectedFristDateTo ?? selectedFristDateFrom,
        "yyyy-MM-dd",
      );

      let fristDateItem: IPersonPaabudFilterSetting["fristDatoer"][number];

      if (isFristDateBetween) {
        fristDateItem = {
          key: `gt-${selectedFristDateFromFormattedPretty}-lt-${selectedFristDateToFormattedPretty}`,
          displayName: `${selectedFristDateFromFormattedPretty} - ${selectedFristDateToFormattedPretty}`,
          selectedQueryName: "Mellem",
          filter: {
            and: [
              {
                indberetningFrist: {
                  gt: selectedFristDateFromFormattedFilter,
                },
              },
              {
                indberetningFrist: {
                  lt: selectedFristDateToFormattedFilter,
                },
              },
            ],
          },
        };
      } else {
        fristDateItem = {
          key: `${selectedFristDateQuery.query}-${selectedFristDateFromFormattedPretty}`,
          displayName: selectedFristDateFromFormattedPretty,
          selectedQueryName: selectedFristDateQuery.displayName,
          filter: {
            indberetningFrist: {
              [selectedFristDateQuery.query]:
                selectedFristDateFromFormattedFilter,
            },
          },
        };
      }

      if (
        !updatedFristDateSettings.some((item) => item.key === fristDateItem.key)
      ) {
        updatedFristDateSettings.push(fristDateItem);
      } else {
        toaster.push(
          <Notification>
            <div className="d-flex flex-row gap-3 align-items-center">
              <span className={`c-theme-icon text-light bg-warning`}>
                <WarningIcon />
              </span>
              Filteret er allerede tilføjet.
            </div>
          </Notification>,
          {
            placement: "bottomEnd",
            duration: 2500,
          },
        );
      }
    }

    const updatedSettings: IPersonPaabudFilterSetting = {
      paabudStatus: paabudStatusValues,
      indberetningStatus: indberetningStatusValues,
      antalLektioner: updatedLektionerSettings,
      modtagetDatoer: updatedRegDatoSettings,
      fristDatoer: updatedFristDateSettings,
      iBero: iBeroCheckboxRef.current?.checked ?? false,
    };

    const canBuildFilter =
      updatedSettings.antalLektioner.length ||
      updatedSettings.modtagetDatoer.length ||
      updatedSettings.fristDatoer.length ||
      updatedSettings.paabudStatus.length ||
      updatedSettings.indberetningStatus.length ||
      updatedSettings.iBero;

    const filterString = canBuildFilter ? buildFilter(updatedSettings) : "";

    const queryFilter: QueryFilter<
      PersonFilterKeys,
      IPersonPaabudFilterSetting
    > = {
      filterKey: filterKey,
      filter: filterString,
      parentFilter: "and",
      settings: updatedSettings,
    };

    if (queryFilter.filter === "") {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    } else {
      updateFilters({
        event: "update",
        queryFilter: queryFilter,
      });
    }
  }

  function removeFilterHandler(
    key: string,
    eventType: "lektioner" | "modtaget" | "frist",
  ) {
    const paabudStatusValues = getCheckboxValues<PersonPaabudStatusId>(
      paabudStatusCheckboxRefs.current,
    );
    const indberetningStatusValues =
      getCheckboxValues<EfteruddannelseIndberetningStatusId>(
        indberetningStatusCheckboxRefs.current,
      );

    const updatedLektionerSettings =
      eventType === "lektioner"
        ? settings?.antalLektioner.filter((set) => set.key !== key)
        : settings?.antalLektioner;

    const updatedRegDatoSettings =
      eventType === "modtaget"
        ? settings?.modtagetDatoer.filter((set) => set.key !== key)
        : settings?.modtagetDatoer;

    const updatedFristDateSettings =
      eventType === "frist"
        ? settings?.fristDatoer.filter((set) => set.key !== key)
        : settings?.fristDatoer;

    const updatedSettings: IPersonPaabudFilterSetting = {
      paabudStatus: paabudStatusValues,
      indberetningStatus: indberetningStatusValues,
      antalLektioner: updatedLektionerSettings,
      modtagetDatoer: updatedRegDatoSettings,
      fristDatoer: updatedFristDateSettings,
      iBero: iBeroCheckboxRef.current?.checked ?? false,
    };

    const canBuildFilter =
      updatedSettings.antalLektioner.length ||
      updatedSettings.modtagetDatoer.length ||
      updatedSettings.fristDatoer.length ||
      updatedSettings.paabudStatus.length ||
      updatedSettings.indberetningStatus.length ||
      updatedSettings.iBero;

    const filterString = canBuildFilter ? buildFilter(updatedSettings) : "";

    const queryFilter: QueryFilter<
      PersonFilterKeys,
      IPersonPaabudFilterSetting
    > = {
      filterKey: filterKey,
      filter: filterString,
      parentFilter: "and",
      settings: updatedSettings,
    };

    if (queryFilter.filter === "") {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    } else {
      updateFilters({
        event: "update",
        queryFilter: queryFilter,
      });
    }
  }

  return (
    <AccordionFilterItem
      title="Påbud"
      value="paabud"
      isActive={filters.some((f) => f.filterKey === filterKey)}
    >
      <h4 className="h6 mb-1 text-secondary">Frist</h4>
      <div className="c-applied-filter-group">
        {settings?.fristDatoer.map(
          (set: IPersonPaabudFilterSetting["fristDatoer"][number]) => {
            return (
              <AppliedFilterItem
                key={set.key}
                labelPrefix={set.selectedQueryName}
                label={set.displayName}
                removeFilterHandler={() => {
                  removeFilterHandler(set.key, "frist");
                }}
              />
            );
          },
        )}
      </div>

      <ComboBox>
        <Select
          defaultValue={selectedFristDateQuery?.query}
          onValueChange={(value: string) => {
            if (value === "between") {
              setIsFristDateBetween(true);
              return;
            } else {
              setIsFristDateBetween(false);
            }

            const query = dateQueries.find((q) => q.query === value);
            if (query) {
              setSelectedFristDateQuery(query);
            }
          }}
        >
          <SelectTrigger aria-label="Vælg filterfunktion" badge />
          <SelectContent small>
            {dateQueries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
            <SelectItem value={"between"} key={"between"}>
              Mellem
            </SelectItem>
          </SelectContent>
        </Select>
        <DatePicker
          appearance="subtle"
          cleanable={false}
          defaultValue={selectedFristDateFrom}
          format="dd-MM-yyyy"
          oneTap
          placement="autoVertical"
          size="xs"
          onChange={(date) => {
            setSelectedFristDateFrom(isCompleteDate(date) ? date : null);
          }}
        />
        {isFristDateBetween && (
          <>
            -{" "}
            <DatePicker
              appearance="subtle"
              cleanable={false}
              defaultValue={selectedFristDateTo}
              format="dd-MM-yyyy"
              oneTap
              placement="autoVertical"
              size="xs"
              onChange={(date) => {
                setSelectedFristDateTo(isCompleteDate(date) ? date : null);
              }}
            />
          </>
        )}
        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1`}
          disabled={
            !selectedFristDateFrom ||
            (isFristDateBetween &&
              (!selectedFristDateTo ||
                selectedFristDateFrom > selectedFristDateTo))
          }
          onClick={() => {
            updateFilterHandler("fristDate");
          }}
        >
          OK
        </button>
      </ComboBox>

      <h4 className="h6 mt-3 mb-1 text-secondary">Påbud status</h4>
      {Object.entries(PersonPaabudStatusId).map(([key, value], index) => {
        return (
          <InputCheckbox
            id={`${value}__${random(0, 1000)}`}
            label={paabudStatusLabels[value]}
            ref={paabudStatusCheckboxRefs.current[index]}
            checked={settings?.paabudStatus.includes(value)}
            onChange={() => updateFilterHandler("paabudStatus")}
            key={key}
          />
        );
      })}

      <h4 className="h6 mt-3 mb-1 text-secondary">Bero</h4>
      <InputCheckbox
        id={`iBero__paabud`}
        label="I bero"
        ref={iBeroCheckboxRef}
        checked={settings?.iBero ?? false}
        onChange={() => updateFilterHandler("iBero")}
      />
      <p className="text-muted small mb-0">
        Påbud, hvor fristen står stille, fordi advokaten ikke er udøvende.
      </p>

      <h4 className="h6 mt-3 mb-1 text-secondary">Indberetningsstatus</h4>
      {Object.entries(EfteruddannelseIndberetningStatusId).map(
        ([key, value], index) => {
          let displayName = startCase(
            value.replaceAll("_", " ").toLocaleLowerCase(),
          );

          displayName = displayName.replaceAll("ae", "æ").replaceAll("oe", "ø");

          return (
            <InputCheckbox
              id={`${value}__${random(0, 1000)}`}
              label={displayName}
              ref={indberetningStatusCheckboxRefs.current[index]}
              checked={settings?.indberetningStatus.includes(value)}
              onChange={() => updateFilterHandler("indberetningStatus")}
              key={key}
            />
          );
        },
      )}

      <h4 className="h6 mt-3 mb-1 text-secondary">Antal lektioner</h4>

      <div className="c-applied-filter-group">
        {settings?.antalLektioner.map(
          (set: IPersonPaabudFilterSetting["antalLektioner"][number]) => {
            return (
              <AppliedFilterItem
                key={set.key}
                labelPrefix={set.selectedQueryName}
                label={set.displayName}
                removeFilterHandler={() => {
                  removeFilterHandler(set.key, "lektioner");
                }}
              />
            );
          },
        )}
      </div>

      <ComboBox>
        <Select
          defaultValue={selectedLektionerQuery?.query}
          onValueChange={(value: string) => {
            if (value === "between") {
              setIsLektionerBetween(true);
              return;
            } else {
              setIsLektionerBetween(false);
            }

            const query = numberQueries.find((q) => q.query === value);
            if (query) {
              setSelectedLektionerQuery(query);
            }
          }}
        >
          <SelectTrigger aria-label="Vælg filterfunktion" badge />
          <SelectContent small>
            {numberQueries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
            <SelectItem value={"between"} key={"between"}>
              Mellem
            </SelectItem>
          </SelectContent>
        </Select>
        <input
          className="form-control"
          type="number"
          placeholder="Antal lektioner"
          ref={inputRef}
          required
          onKeyUp={(event) => {
            if (event.key === "Enter") {
              updateFilterHandler("lessons");
            }
          }}
        />
        {isLektionerBetween && (
          <>
            -{" "}
            <input
              className="form-control"
              type="number"
              placeholder="Antal lektioner"
              ref={inputToRef}
              required
              onKeyUp={(event) => {
                if (event.key === "Enter") {
                  updateFilterHandler("lessons");
                }
              }}
            />
          </>
        )}
        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1 btn--combo-action`}
          onClick={() => {
            updateFilterHandler("lessons");
          }}
        >
          OK
        </button>
      </ComboBox>

      <h4 className="h6 mt-3 mb-1 text-secondary">Modtaget dato</h4>
      <div className="c-applied-filter-group">
        {settings?.modtagetDatoer.map(
          (set: IPersonPaabudFilterSetting["modtagetDatoer"][number]) => {
            return (
              <AppliedFilterItem
                key={set.key}
                labelPrefix={set.selectedQueryName}
                label={set.displayName}
                removeFilterHandler={() => {
                  removeFilterHandler(set.key, "modtaget");
                }}
              />
            );
          },
        )}
      </div>

      <ComboBox>
        <Select
          defaultValue={selectedReceivedDateQuery?.query}
          onValueChange={(value: string) => {
            if (value === "between") {
              setIsReceivedDateBetween(true);
              return;
            } else {
              setIsReceivedDateBetween(false);
            }

            const query = dateQueries.find((q) => q.query === value);
            if (query) {
              setSelectedReceivedDateQuery(query);
            }
          }}
        >
          <SelectTrigger aria-label="Vælg filterfunktion" badge />
          <SelectContent small>
            {dateQueries.map((p) => {
              return (
                <SelectItem value={p.query} key={p.query}>
                  {p.displayName}
                </SelectItem>
              );
            })}
            <SelectItem value={"between"} key={"between"}>
              Mellem
            </SelectItem>
          </SelectContent>
        </Select>
        <DatePicker
          appearance="subtle"
          cleanable={false}
          defaultValue={selectedReceivedDateFrom}
          format="dd-MM-yyyy"
          oneTap
          placement="autoVertical"
          size="xs"
          onChange={(date) => {
            setSelectedReceivedDateFrom(isCompleteDate(date) ? date : null);
          }}
        />
        {isReceivedDateBetween && (
          <>
            -{" "}
            <DatePicker
              appearance="subtle"
              cleanable={false}
              defaultValue={selectedReceivedDateTo}
              format="dd-MM-yyyy"
              oneTap
              placement="autoVertical"
              size="xs"
              onChange={(date) => {
                setSelectedReceivedDateTo(isCompleteDate(date) ? date : null);
              }}
            />
          </>
        )}
        <button
          aria-label="Anvend"
          className={`btn btn-primary btn--sm py-1 me-n1`}
          disabled={
            !selectedReceivedDateFrom ||
            (isReceivedDateBetween &&
              (!selectedReceivedDateTo ||
                selectedReceivedDateFrom > selectedReceivedDateTo))
          }
          onClick={() => {
            updateFilterHandler("receivedDate");
          }}
        >
          OK
        </button>
      </ComboBox>
    </AccordionFilterItem>
  );
}
